/**
 * UpdateActionEditForm.helpers — value plumbing shared by the read-only
 * property diff (UpdateActionCard) and its editable counterpart
 * (UpdateActionEditForm).
 *
 * Deliberately contains NO value formatting. Rendering a property value is the
 * job of the canonical CRM display components, reached through
 * {@link ProposalPropertyValue}; a card-local stringifier here is exactly the
 * divergence `docs/claude/FRONTEND_PATTERNS.md` § CRM Property & Association
 * Display Components forbids.
 */

import { extractValue } from './shared/utils';

export const STAGE_PROP_PATTERN = /stage|lifecycle/i;

export function isStageProperty(key: string): boolean {
  return STAGE_PROP_PATTERN.test(key);
}

/** The places a property's *current* value can come from. */
export interface CurrentValueSources {
  /** Snapshot taken when the proposal was created (payload `original_values`). */
  originalValues: Record<string, unknown>;
  /** Backend enrichment snapshot (`enrichedData.current_values`). */
  currentValues?: Record<string, unknown> | null;
  /** Live CRM store properties (`useCrmEntityProps`). */
  crmFlatProps?: Record<string, unknown>;
  /** Live CRM store associations, for stage properties (`useCurrentStageValue`). */
  currentStageValues: Record<string, unknown>;
}

/**
 * Resolve the "Current" column value for one property.
 *
 * The live CRM record is the answer whenever we have one. The two snapshots
 * (`original_values` on the payload, `current_values` from backend enrichment)
 * are copies taken when the proposal was written: they answer "what was true
 * then", which is a different question, and they drift the moment the record
 * changes. They are consulted ONLY when there is no live record to read —
 * an update to an entity a sibling action creates, where nothing exists yet.
 *
 * Note the live branch does not fall through to a snapshot when the property
 * is absent: on a record we can see, an unset property is genuinely unset, and
 * falling through would resurrect a stale value as though it were current.
 *
 * Both the read card and the edit form must agree on this chain — when they
 * drifted, entering edit mode could silently change which value the user was
 * comparing against.
 */
export function resolveCurrentValue(
  key: string,
  {
    originalValues,
    currentValues,
    crmFlatProps,
    currentStageValues,
  }: CurrentValueSources
): unknown {
  if (crmFlatProps !== undefined) {
    // Stage values live on associations, not properties, so they are the
    // live source for their own keys rather than a fallback.
    return crmFlatProps[key] ?? currentStageValues[key];
  }
  return (
    extractValue(originalValues[key]) ??
    extractValue(currentValues?.[key]) ??
    currentStageValues[key]
  );
}

/**
 * Field types whose `PropertyValueLabel` renderer IGNORES `maxLength` — the
 * specialized label components always render their full value. A diff-row
 * "Show more" toggle on one of these is a button that does nothing: the row
 * is already showing everything, and toggling `maxLength` off changes no
 * output.
 *
 * Kept as a blocklist (mirroring the non-default cases of
 * `PropertyValueLabel`'s field-type switch) rather than an allowlist so an
 * unknown/new field type — which falls through to `TextValueLabel`, where
 * `maxLength` DOES truncate — keeps a working toggle. The failure mode of a
 * stale entry here is a truncated value with no way to expand it, which is
 * worse than a redundant toggle; update this set when a specialized label
 * gains or loses `maxLength` support.
 *
 * `number` and `formula` are deliberately absent: their renderers honor
 * `maxLength`, as does the default text path.
 */
const MAXLENGTH_BLIND_FIELD_TYPES = new Set([
  'phone_number',
  'email',
  'currency',
  'multi_currency',
  'boolean',
  'select',
  'timezone',
  'language',
  'date',
  'datetime',
  'duration_days',
  'file_size',
  'url',
  'markdown',
  'manifest',
  'ahoy_report_query',
  'working_hours_schedule',
  'booking_questions',
  'permissions',
  'location_type',
  'color',
  'permission_group',
  'association_type',
  'entity_type',
  'domain',
]);

/**
 * Length of the text a value will render as, used to decide whether a diff row
 * needs an expand/collapse toggle.
 *
 * Returns 0 (never expandable) unless BOTH hold:
 * - the value is a measurable primitive (arrays/objects render as chips or
 *   composed labels that ignore `maxLength`), AND
 * - the property's field type actually truncates via `maxLength` (see
 *   {@link MAXLENGTH_BLIND_FIELD_TYPES}). A long markdown description or URL
 *   is a string too, but its renderer always shows the full value — offering
 *   "Show more" there is a button that does nothing.
 */
export function propertyValueTextLength(
  value: unknown,
  fieldTypeName?: string
): number {
  if (
    fieldTypeName !== undefined &&
    MAXLENGTH_BLIND_FIELD_TYPES.has(fieldTypeName)
  ) {
    return 0;
  }
  const unwrapped = extractValue(value);
  if (typeof unwrapped === 'string') {
    return unwrapped.length;
  }
  if (typeof unwrapped === 'number' || typeof unwrapped === 'boolean') {
    return String(unwrapped).length;
  }
  return 0;
}
